import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import {
  Box,
  Flex,
  FormControl,
  Select,
  Text,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Input,
  FormErrorMessage,
  useToast,
  Textarea,
  HStack,
  Stack,
} from '@chakra-ui/core';
import { ChevronDownIcon } from '@chakra-ui/icons';
import {
  MdCheckBoxOutlineBlank,
  MdCheckBox,
  MdOutlineRadioButtonUnchecked,
  MdOutlineRadioButtonChecked,
  MdDragIndicator,
} from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import Joi from 'joi';
import { IoClose } from 'react-icons/io5';
import ResizeTextarea from 'react-textarea-autosize';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import { CorrectAnswerDeterminationType, JobPosition, MultipleChoiceImage, MultipleChoiceQuestion } from '../PositionType';
import colors from '../../../../styles/colors';
import { errorFormat, FormattedError, getErrorMessage, hasErrorMessage } from '../../../../utils/FormErrorUtils';
import { SortEndType } from './QuestionsListView';

import theme from '../../../../styles/customTheme';
import SelectMCQImage from './SelectMCQImage';
import QuestionKnockoutReview from './QuestionKnockoutReview';
import { QuestionsTypes } from './QuestionsTypes';

const MAX_OPTIONS_COUNT = 6;

/* istanbul ignore next */
function swap<T>(array: T[], indexA: number, indexB: number): T[] {
  const newArray = [...array]; // Spread with a known type
  [newArray[indexA], newArray[indexB]] = [newArray[indexB], newArray[indexA]];
  return newArray;
}

const onGetIcon = (label: number, numberOfOptions: string, answersList: number[]) => {
  if (parseInt(numberOfOptions, 10) === 1 && numberOfOptions !== 'any') {
    if (answersList.includes(label)) {
      return <MdOutlineRadioButtonChecked style={{ color: colors.blue[500] }} />;
    }
    return <MdOutlineRadioButtonUnchecked style={{ color: colors.blue[500] }} />;
  }
  if (answersList.includes(label)) {
    return <MdCheckBox style={{ color: colors.blue[500] }} />;
  }
  return <MdCheckBoxOutlineBlank style={{ color: colors.blue[500] }} />;
};

export type MultipleChoiceQuestionsCardProps = {
  jobPositionData: JobPosition;
  addMultipleChoiceQuestion: (question: MultipleChoiceQuestion, questionEditMode: number | undefined) => void;
  deleteMultipleQuestionsQuestion: (questionId: number) => void;
  questionToEdit: MultipleChoiceQuestion;
  questionToEditMode: number | undefined;
  onRefreshState: () => void;
  questionType: QuestionsTypes;
};

interface InputData {
  id: number;
  value: string;
}

export const MultipleChoiceQuestionsCard = ({
  addMultipleChoiceQuestion,
  questionToEdit,
  questionToEditMode,
}: MultipleChoiceQuestionsCardProps): JSX.Element => {
  const { t } = useTranslation();
  const toast = useToast();
  const [numberOfOptions, setNumberOfOptions] = useState<string>('1');
  const [atleast, setAtlest] = useState<string>('1');
  const [selectAnswers, setSelectAnswers] = useState<boolean>(false);
  const [knockout, setKnockout] = useState<boolean>(false);
  const [isPausable, setIsPausable] = useState<boolean>(false);
  const [errorOptionsList, setErrorOptionsList] = useState<boolean>(false);
  const [answersList, setAnswersList] = useState<number[]>([]);
  const [questions, setQuestion] = useState<string>('');
  const [questionImage, setQuestionImage] = useState<MultipleChoiceImage[]>([]);
  const [questionEditMode, setQuestionEditMode] = useState<number | undefined>(undefined);
  const [correctAnswerDeterminationType, setCorrectAnswerDeterminationType] = useState<
    CorrectAnswerDeterminationType | undefined
  >(CorrectAnswerDeterminationType.IS_EQUAL);
  const [queFormErrors, setQueFormErrors] = useState<FormattedError[]>([]);
  const textAreaRef = useRef() as React.MutableRefObject<HTMLInputElement>;
  const [inputs, setInputs] = useState<InputData[]>([
    { id: 1, value: '' },
    { id: 2, value: '' },
  ]);

  /* istanbul ignore next */
  const addOption = () => {
    const newItem = {
      id: inputs[inputs.length - 1].id + 1,
      value: '',
      image: null,
      isTextOnFront: false,
    };
    setInputs((prevInputs) => [...prevInputs, newItem]);
  };
  const removeOption = (id: number, label: number) => {
    let newAnswerList = [...answersList];
    if (newAnswerList.includes(label)) {
      newAnswerList = newAnswerList.filter((answer) => answer !== label);
    }
    setAnswersList(newAnswerList);
    setInputs((prevInputs) => prevInputs.filter((input) => input.id !== id));
  };

  const onGetOptionTypeByPriority = (priority: number) => {
    const maxPriority = Math.max(MAX_OPTIONS_COUNT);
    return String.fromCharCode(65 + (priority % maxPriority)).toUpperCase();
  };

  const editQuestion = (questionId: number | undefined, ques: MultipleChoiceQuestion) => {
    setQuestion(ques.question);
    setQuestionImage(ques.images);
    setNumberOfOptions(ques.totalAnswer.toString());
    setCorrectAnswerDeterminationType(ques?.correctAnswerIf);
    setKnockout(ques.knockout);
    setIsPausable(ques?.isPausable || /* istanbul ignore next */ false);
    const options: InputData[] = [];
    const ansList: number[] = [];
    ques.options.forEach((q, index) => {
      const label = onGetOptionTypeByPriority(q.priority);
      const o = { id: index, value: q.text, label };
      if (q.isPreferredAnswer) {
        ansList.push(index);
      }
      options.push(o);
    });
    setInputs(options);
    setAnswersList(ansList);
    setAtlest(ques?.shouldMatchAtLeast?.toString() ?? /* istanbul ignore next */ '1');
    setQuestionEditMode(questionId);
  };

  useEffect(() => {
    editQuestion(questionEditMode, questionToEdit);
    setQuestionEditMode(questionToEditMode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionToEdit, questionToEditMode]);

  const imageSchema = Joi.object({
    galleryId: Joi.string().required(),
    url: Joi.string().required(),
    path: Joi.string().optional(),
  });

  const optionSchema = Joi.object({
    text: Joi.string()
      .required()
      .messages({
        'string.empty': t('createJobPosition:optionNotEmpty'),
      }),
    isPreferredAnswer: Joi.boolean(),
    priority: Joi.number(),
  });

  const multipleQuestionSchema = Joi.object().keys({
    question: Joi.string()
      .trim(true)
      .max(500)
      .required()
      .messages({
        'string.max': t('createJobPosition:questionMaxLetterRequired', { count: 500 }),
        'string.empty': t('createJobPosition:questionNotEmpty'),
      }),
    images: Joi.array().items(imageSchema),
    knockout: Joi.boolean().optional(),
    isPausable: Joi.boolean().optional(),
    options: Joi.array().items(optionSchema),
    isActive: Joi.boolean().optional(),
    correctAnswerIf: Joi.string().optional(),
    totalAnswer: Joi.alternatives().try(Joi.number(), Joi.string()).required(),
    shouldMatchAtLeast: Joi.number().optional(),
  });

  const queFormValidation = (formData: any) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment

    const { error } = multipleQuestionSchema.validate(formData);

    if (error) {
      setQueFormErrors(errorFormat(error.details));
      return false;
    }
    if (
      (numberOfOptions === 'any' && answersList.length === 0) ||
      (numberOfOptions !== 'any' && answersList.length < parseInt(numberOfOptions, 10))
    ) {
      setErrorOptionsList(true);
      toast({
        title: t('createJobPosition:optionsRequired', {
          numberOfOptions: numberOfOptions === 'any' ? 1 : numberOfOptions,
        }),
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }
    setErrorOptionsList(false);
    setQueFormErrors([]);
    return true;
  };

  const clearFormData = () => {
    setQuestion('');
    setQuestionImage([]);
    setNumberOfOptions('1');
    setCorrectAnswerDeterminationType(CorrectAnswerDeterminationType.IS_EQUAL);
    setKnockout(false);
    setIsPausable(false);
    setInputs([
      { id: 1, value: '' },
      { id: 2, value: '' },
    ]);
    setAnswersList([]);
    setQuestionEditMode(undefined);
    setErrorOptionsList(false);
  };

  const handleInputChange = (id: number) => (event: ChangeEvent<HTMLInputElement>) => {
    // istanbul ignore else
    if (event.target.value.length <= 120) {
      const newInputs = inputs.map((input) => (input.id === id ? { ...input, value: event.target.value } : input));
      setInputs(newInputs);
    }
  };

  // eslint-disable-next-line consistent-return
  const createQuestion = () => {
    const options: {
      text: string;
      isPreferredAnswer: boolean;
      priority: number;
    }[] = [];
    inputs.forEach((i, index) => {
      options.push({
        text: i.value.trim(),
        isPreferredAnswer: answersList.includes(index),
        priority: index,
      });
    });
    const multipleChoiseQuestionData: MultipleChoiceQuestion = {
      question: questions.trim(),
      images: questionImage,
      knockout,
      isPausable,
      totalAnswer: numberOfOptions === 'any' ? numberOfOptions : parseInt(numberOfOptions, 10),
      options,
      shouldMatchAtLeast:
        CorrectAnswerDeterminationType.AT_LEAST === correctAnswerDeterminationType && atleast
          ? parseInt(atleast ?? /* istanbul ignore next */ '1', 10)
          : /* istanbul ignore next */ undefined,
      correctAnswerIf: correctAnswerDeterminationType,
    };
    const validate = queFormValidation(multipleChoiseQuestionData);
    if (!validate) {
      return false;
    }
    addMultipleChoiceQuestion(multipleChoiseQuestionData, questionEditMode);
    setQuestionEditMode(undefined);
    clearFormData();
  };

  /* istanbul ignore next */
  const onSortEnd = ({ oldIndex, newIndex }: SortEndType) => {
    setInputs(swap(inputs, oldIndex, newIndex));
  };

  return (
    <Box w="-webkit-fill-available" position="relative" px={2}>
      <Box>
        <Flex w="calc(80% + 58px)">
          <Text fontSize="14px" display="flex" alignItems="center" fontWeight="700">
            {t('createJobPosition:pleaseSelect')}
          </Text>
          {/* <FormControl > */}
          <Select
            // fontSize="14px"
            // fontWeight="700"
            // className="filled"
            variant="outline"
            size="sm"
            borderRadius="md"
            background="white"
            value={numberOfOptions}
            data-testid="numberOfOptions"
            onChange={(e) => {
              setAnswersList([]);
              setErrorOptionsList(false);
              setNumberOfOptions(e.target.value);
            }}
            w="auto"
            marginLeft="8px"
          >
            {inputs.map((e, index) => (
              <option value={(index + 1).toString()} key={e.id}>
                {index + 1}
              </option>
            ))}
            <option value="any">{t('createJobPosition:any')}</option>
          </Select>
          {/* </FormControl> */}
          <Text marginLeft="8px" display="flex" alignItems="center" fontSize="14px" fontWeight="700">
            {t('createJobPosition:bestOptions')}
          </Text>

          {/* <FormControl w="20%" marginLeft="8px"> */}
          <Select
            variant="outline"
            size="sm"
            borderRadius="md"
            background={colors.white}
            w="auto"
            marginLeft="8px"
            value={correctAnswerDeterminationType}
            data-testid="correctAnswerDeterminationType"
            onChange={(e) => {
              setErrorOptionsList(false);
              setCorrectAnswerDeterminationType(e.target.value as CorrectAnswerDeterminationType);
            }}
          >
            <option value={CorrectAnswerDeterminationType.IS_EQUAL}>
              {t('createJobPosition:multipleChoiceQuestion.IS_EQUAL')}
            </option>
            {parseInt(numberOfOptions, 10) !== 1 && (
              <option value={CorrectAnswerDeterminationType.HAS_ANSWER}>
                {t('createJobPosition:multipleChoiceQuestion.HAS_ANSWER')}
              </option>
            )}
            <option value={CorrectAnswerDeterminationType.DOES_NOT_HAVE_ANSWER}>
              {t('createJobPosition:multipleChoiceQuestion.DOES_NOT_HAVE_ANSWER')}
            </option>
            <option value={CorrectAnswerDeterminationType.AT_LEAST}>
              {t('createJobPosition:multipleChoiceQuestion.AT_LEAST')}
            </option>
          </Select>
          {/* </FormControl> */}

          {CorrectAnswerDeterminationType.AT_LEAST === correctAnswerDeterminationType && (
            // <FormControl w="6%" marginLeft="8px">
            <Select
              variant="outline"
              size="sm"
              borderRadius="md"
              background={colors.white}
              w="auto"
              marginLeft="8px"
              value={atleast}
              data-testid="atleast"
              onChange={(e) => setAtlest(e.target.value)}
            >
              {parseInt(numberOfOptions, 10) === 1 ? (
                <option value="1">1</option>
              ) : (
                inputs.map((e, index) => (
                  <option value={(index + 1).toString()} key={e.id}>
                    {index + 1}
                  </option>
                ))
              )}
            </Select>
            // </FormControl>
          )}
          {CorrectAnswerDeterminationType.AT_LEAST === correctAnswerDeterminationType && (
            <Text fontSize="14px" display="flex" alignItems="center" marginLeft="8px" fontWeight="700">
              {t('createJobPosition:of')}
            </Text>
          )}
          <FormControl w="auto" marginLeft="8px">
            <Menu isOpen={selectAnswers} onClose={/* istanbul ignore next */ () => setSelectAnswers(false)} closeOnSelect={false}>
              <MenuButton
                // eslint-disable-next-line
                as={Button as any}
                variant="outline"
                rightIcon={<ChevronDownIcon fontSize="20px" />}
                borderRadius="md"
                background={colors.white}
                borderColor={errorOptionsList ? 'red' : '#a1b7c2'}
                // fontSize="14px"
                fontWeight="normal"
                // height="48px"
                size="sm"
                display="flex"
                alignItems="center"
                data-testid="selectAnswerBtn"
                onClick={() => setSelectAnswers(true)}
              >
                {answersList.length === 0
                  ? t('createJobPosition:selectOptions')
                  : answersList.map((num) => String.fromCharCode('A'.charCodeAt(0) + num)).join(', ')}
              </MenuButton>
              <MenuList>
                {inputs.map((e, index) => (
                  <MenuItem
                    _focus={{ background: '#ffffff' }}
                    key={e.id}
                    data-testid={`selectAnswerBtn_${e.id}`}
                    onClick={() => {
                      let newAnswerList = [...answersList];
                      /* istanbul ignore next */
                      if (parseInt(numberOfOptions, 10) === 1) {
                        if (CorrectAnswerDeterminationType.AT_LEAST !== correctAnswerDeterminationType) {
                          newAnswerList = [index];
                        } else if (newAnswerList.includes(index)) {
                          newAnswerList = newAnswerList.filter((answer) => answer !== index);
                        } else {
                          newAnswerList.push(index);
                        }
                      } else if (newAnswerList.includes(index)) {
                        newAnswerList = newAnswerList.filter((answer) => answer !== index);
                      } else if (parseInt(numberOfOptions, 10) !== newAnswerList.length) {
                        newAnswerList.push(index);
                      } else if (CorrectAnswerDeterminationType.AT_LEAST === correctAnswerDeterminationType) {
                        newAnswerList.push(index);
                      }

                      setAnswersList(newAnswerList);
                    }}
                  >
                    {onGetIcon(index, numberOfOptions, answersList)}
                    <Text fontSize="14px" mt="2px" ml="10px" fontWeight="700">
                      {onGetOptionTypeByPriority(index)}
                    </Text>
                  </MenuItem>
                ))}

                <MenuItem display="flex" justifyContent="end" _focus={{ background: '#ffffff' }}>
                  <Button colorScheme="blue" data-testid="doneBtn" onClick={() => setSelectAnswers(false)}>
                    {t('createJobPosition:done')}
                  </Button>
                </MenuItem>
              </MenuList>
            </Menu>
          </FormControl>
        </Flex>
      </Box>
      <Flex mt="14px">
        <Text fontSize="14px" color="#607D8B">
          {t('createJobPosition:hintText')}
        </Text>
        {/* <Tooltip placement="top" shouldWrapChildren label={t('createJobPosition:hintText')}> */}
        {/*  <BsFillInfoCircleFill /> */}
        {/* </Tooltip> */}
      </Flex>
      <Box mt="8px">
        <FormControl isInvalid={hasErrorMessage(queFormErrors, 'question')} ref={textAreaRef} className="outline-input">
          <Flex justifyContent="space-between" w="calc(80% + 58px)">
            <Textarea
              background={colors.white}
              minH="unset"
              maxH="120px"
              minRows={1}
              maxRows={5}
              as={ResizeTextarea}
              placeholder={t('createJobPosition:mcqQuestionPlaceholder')}
              value={questions}
              onChange={(e) => {
                // istanbul ignore else
                if (e.target.value.length <= 350) {
                  setQuestion(e.target.value);
                }
              }}
              _placeholder={{
                fontSize: 'sm',
              }}
              data-testid="questions"
              resize="none"
              flexGrow={1}
              onFocus={() => setSelectAnswers(false)}
            />
            <SelectMCQImage images={questionImage} onSelect={setQuestionImage} />
          </Flex>

          <FormErrorMessage>{getErrorMessage(queFormErrors, 'question')}</FormErrorMessage>
        </FormControl>
      </Box>
      <Box>
        <SortableMCQOptions
          {...{
            inputs,
            queFormErrors,
            numberOfOptions,
            answersList,
            handleInputChange,
            removeOption,
          }}
          distance={20}
          onSortEnd={onSortEnd}
          axis="y"
          lockAxis="y"
          hideSortableGhost={false}
          onFocus={() => setSelectAnswers(false)}
        />
        {inputs.length < MAX_OPTIONS_COUNT && ( //
          <Text
            width="fit-content"
            mt={4}
            textDecoration="underline"
            _hover={{ color: colors.blue[500] }}
            cursor="pointer"
            color="#607D8B"
            fontSize={16}
            fontWeight={700}
            onClick={addOption}
            data-testid="addOptionBtn"
          >
            {t('createJobPosition:addOption')}
          </Text>
        )}
      </Box>
      <Stack mt={4} spacing={3}>
        <QuestionKnockoutReview
          value={{
            isPausable,
            knockout,
          }}
          onChange={(value) => {
            setIsPausable(value?.isPausable ?? /* istanbul ignore next */ false);
            setKnockout(value?.knockout ?? /* istanbul ignore next */ false);
          }}
        />
      </Stack>
      <Flex
        justifyContent="end"
        style={{
          width: '100%',
          position: 'sticky',
          bottom: 0,
          zIndex: 1,
        }}
      >
        <Button
          colorScheme="blue"
          // disabled={questions.text.length < 9}
          data-testid="addQuestionBtn"
          onClick={() => createQuestion()}
        >
          {t('createJobPosition:confirmBtn')}
        </Button>
      </Flex>
    </Box>
  );
};

interface SortableMCQOptionsProps {
  inputs: InputData[];
  queFormErrors: FormattedError[];
  numberOfOptions: string;
  answersList: number[];
  handleInputChange: (id: number) => (event: ChangeEvent<HTMLInputElement>) => void;
  removeOption: (id: number, label: number) => void;
  onFocus: React.FocusEventHandler<HTMLInputElement>;
}

const SortableMCQOptions = SortableContainer(function (props: SortableMCQOptionsProps) {
  return (
    <Box>
      {props.inputs.map((input, index) => {
        return (
          <MCQOption
            {...props}
            key={input.id}
            index={index}
            optionIndex={index}
            input={input}
            totalOptions={props.inputs.length}
          />
        );
      })}
    </Box>
  );
});

const MCQOption = SortableElement(function ({
  input,
  inputs,
  queFormErrors,
  numberOfOptions,
  answersList,
  handleInputChange,
  removeOption,
  optionIndex,
  totalOptions,
  onFocus,
}: SortableMCQOptionsProps & { input: InputData; optionIndex: number; totalOptions: number }) {
  const onGetOptionTypeByPriority = (priority: number) => {
    const maxPriority = Math.max(MAX_OPTIONS_COUNT);
    return String.fromCharCode(65 + (priority % maxPriority)).toUpperCase();
  };

  const isNonRemovableOption = totalOptions <= 2;
  return (
    <Flex w="calc(80% + 58px)" alignItems="center" mt="12px" cursor="grab">
      <FormControl isInvalid={hasErrorMessage(queFormErrors, `options`)} className="outline-input">
        <HStack spacing={4} alignItems="stretch">
          <Flex h="inherit" alignItems="center">
            <MdDragIndicator color={theme.colors.gray[300]} id="mcq-option-grab" />
          </Flex>
          <HStack spacing={2} backgroundColor={theme.colors.blue[30]} borderRadius={theme.radii.md} py={1} px={2}>
            <Text fontsize="14px">{onGetOptionTypeByPriority(optionIndex)}</Text>
            {onGetIcon(optionIndex, numberOfOptions, answersList)}
          </HStack>
          <Input
            minH="32px"
            background="white"
            _placeholder={{ fontSize: 'sm' }}
            minRows={1}
            maxRows={3}
            height="32px !important"
            paddingY="5px"
            as={ResizeTextarea}
            placeholder={`Option ${onGetOptionTypeByPriority(optionIndex)}`}
            data-testid={`Option-${onGetOptionTypeByPriority(optionIndex)}`}
            value={input.value}
            onChange={handleInputChange(input.id)}
            resize="none"
            onFocus={onFocus}
          />
        </HStack>
        {optionIndex === inputs.length - 1 && <FormErrorMessage>{getErrorMessage(queFormErrors, `options`)}</FormErrorMessage>}
      </FormControl>
      <Flex
        ml={4}
        height="32px"
        width="42px"
        flexShrink={0}
        cursor={isNonRemovableOption ? 'not-allowed' : 'pointer'}
        alignItems="center"
        justifyContent="center"
        borderRadius={theme.radii.md}
        onClick={() => {
          // istanbul ignore next
          if (!isNonRemovableOption) {
            removeOption(input.id, optionIndex);
          }
        }}
        data-testid={`removeOption-${optionIndex}`}
      >
        <IoClose color={isNonRemovableOption ? colors.gray[300] : colors.red[500]} />
      </Flex>
    </Flex>
  );
});
